import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Flame, Newspaper } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Card } from "@/components/Layout";

const Question = ({ q, n, pick, onPick, r }) => {
  const { t } = useApp();
  return (
    <div className="space-y-2" data-testid={`quiz-${q.id}`}>
      {q.news && <p className="flex items-center gap-1 text-[11px] font-bold uppercase text-[#E05C3A]"><Newspaper className="h-3 w-3" />{t("From this week's news")}</p>}
      <p className="font-semibold text-[#0D3B2B]">{n + 1}. {t(q.q)}</p>
      {q.options.map((o, i) => {
        const mark = r && (i === r.answer ? "border-[#2E9E6B] bg-[#2E9E6B]/10" : pick === i ? "border-[#E05C3A] bg-[#FDF0EC]" : "border-[#D2E0D8]");
        return (
          <button key={i} disabled={!!r} onClick={() => onPick(i)} data-testid={`quiz-${q.id}-opt-${i}`}
            className={`flex w-full items-center justify-between rounded-xl border-2 px-3 py-2 text-left text-sm transition-colors ${mark || (pick === i ? "border-[#0D3B2B]" : "border-[#D2E0D8]")}`}>
            {t(o)}{r && i === r.answer && <CheckCircle2 className="h-4 w-4 text-[#2E9E6B]" />}{r && pick === i && i !== r.answer && <XCircle className="h-4 w-4 text-[#E05C3A]" />}
          </button>
        );
      })}
      {r?.why && <p className="rounded-xl bg-[#F4F8F5] p-2 text-xs text-[#4A6358]" data-testid={`quiz-${q.id}-why`}>{t(r.why)}</p>}
    </div>
  );
};

export const DailyQuiz = () => {
  const { t, user } = useApp();
  const [d, setD] = useState(null);
  const [ans, setAns] = useState({});
  const [res, setRes] = useState(null);
  const load = useCallback(() => api.get("/learn/quiz").then((r) => { setD(r.data); setAns({}); setRes(null); }).catch(() => {}), []);
  useEffect(() => { if (user) load(); }, [user, load]);
  if (!user) return <Card data-testid="quiz-login"><Link to="/login" className="font-semibold text-[#2E9E6B]">{t("Log in to take the daily quiz and earn Green Points")}</Link></Card>;
  if (!d) return null;
  const submit = async () => {
    try { const { data } = await api.post("/learn/quiz", { answers: ans }); setRes(data); toast.success(`${t("You earned")} ${data.points_earned} ${t("Green Points")}`); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const streak = res?.streak ?? d.streak;
  return (
    <Card className="space-y-4" data-testid="quiz">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h2 className="font-display text-lg font-bold text-[#0D3B2B]">{t("Daily quiz")}</h2>
          <p className="text-xs text-[#4A6358]">{d.points_per} {t("points per correct answer")} · +{d.streak_bonus} {t("for playing each day")}</p>
        </div>
        <span className="flex shrink-0 items-center gap-1 rounded-full bg-[#FDF0EC] px-3 py-1 text-sm font-bold text-[#E05C3A]" data-testid="quiz-streak"><Flame className="h-4 w-4" />{streak} {t("day streak")}</span>
      </div>
      {!d.questions.length && (
        <p className="rounded-xl bg-[#F4F8F5] p-3 text-sm text-[#0D3B2B]" data-testid="quiz-done-today">
          {d.left_in_bank ? t("You've finished today's quiz. Come back tomorrow for new questions!") : t("You've answered every question we have! New questions from the news arrive every week.")}
        </p>
      )}
      {d.questions.map((q, n) => <Question key={q.id} q={q} n={n} pick={ans[q.id]} r={res?.results[q.id]} onPick={(i) => setAns({ ...ans, [q.id]: i })} />)}
      {!!d.questions.length && !res && <button onClick={submit} disabled={!Object.keys(ans).length} className="h-12 w-full rounded-2xl bg-[#0D3B2B] font-bold text-white disabled:opacity-40" data-testid="quiz-submit-btn">{t("Check answers")}</button>}
      {res && (res.left_today > 0
        ? <button onClick={load} className="h-12 w-full rounded-2xl border-2 border-[#0D3B2B] font-bold text-[#0D3B2B]" data-testid="quiz-next-btn">{t("Next questions")}</button>
        : <p className="text-center text-sm font-semibold text-[#2E9E6B]" data-testid="quiz-come-back">{t("Great job! New questions tomorrow.")}</p>)}
    </Card>
  );
};
